import { reactive, UnwrapRef, ref, watch, Ref, computed, ComputedRef, onMounted } from '@vue/composition-api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { TreeNodeModel } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import logExport from '@/renderer/scripts/utils/LogExporter';
import { IS_BROWSER } from '@/common/constant/platform';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { getDownloadPath } from '@/renderer/scripts/utils/download/downloadHelper';
import { exportCSVInBrowser, exportCSVInPC } from '@/renderer/scripts/utils/tools/index';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import { getCheckManagementPermission } from '@/renderer/scripts/utils/UserRole';
import { isAdminOrSuperAdmin, isTeamAdmin } from '@/renderer/scripts/utils/UserRole/getters';
dayjs.extend(utc);
type TableData = {
  searchId: string;
  loading: boolean;
  hasMore: boolean;
  searchFinished: boolean; // 异步搜索是否完成
  nextMarker: number;
  list: SMH.SearchLogItem[];
};
interface Options {
  label: string;
  value: string;
}
interface SpaceOptionTeamChild {
  label: string;
  value: string;
  children?: boolean | SpaceOptionTeamChild[];
}
interface SpaceOption {
  label: string;
  value: string;
  children?: boolean | SpaceOptionTeamChild[];
}
export interface ListDownloadLogTaskContentItem {
  name: string;
  status: number;
  total: number;
  remaining: number;
  taskId: number;
  creationTime: string;
  time?: string;
  target: 'local' | 'cloud';
  spaceId?: SMH.SpaceId;
  teamId?: SMH.TeamId;
  userId?: SMH.UserId;
  path?: string[];
}
export interface DownloadLogTransferList {
  exporting: ListDownloadLogTaskContentItem[];
  exported: ListDownloadLogTaskContentItem[];
}
type Handler = {
  spaceOptions: ComputedRef<SpaceOption[]>;
  actionList: Options[];
  disabledDate: string;
  timeRange: Ref<string[]>;
  conditions: SMH.CreateSearchLogTaskParams;
  columns: Array<any>;
  tableData: UnwrapRef<TableData>;
  loadingStatus: ComputedRef<null | 'loading' | 'load-more'>;
  localTransferList: DownloadLogTransferList;
  showExportingMsg: Ref<boolean>;
  isAdminOrSuperAdmin: ComputedRef<boolean>;
  methods: {
    startSearch: () => void;
    continueSearch: () => Promise<void>;
    initConditions: () => void;
    exportList: () => Promise<void>;
    exportAll: () => void;
    export2Cloud: (spaceId: string, dirPath: string) => void;
    onScroll: (params: { e: WheelEvent }) => void;
    pullTeamNode: (param: TreeNodeModel<any>) => Promise<SpaceOptionTeamChild[]>;
    getTeamNamePath: (id: SMH.TeamId) => string;
    setTeamOptions: () => void;
  };
};
export default function pullListHandler(): Handler {
  const columns = [
    {
      colKey: 'operationTime',
      title: i18n_t('操作时间'),
      width: 200,
      fixed: 'left',
    },
    {
      colKey: 'operatorName',
      title: i18n_t('操作者'),
      ellipsis: { disable: true, content: '', hideEmptyPopup: true },
      width: 120,
    },
    {
      colKey: 'action',
      title: i18n_t('操作类型'),
      ellipsis: true,
      width: 120,
    },
    {
      colKey: 'objectName',
      title: i18n_t('操作对象'),
      ellipsis: true,
      width: i18n.locale === 'zh-CN' ? 120 : 140,
    },
    {
      colKey: 'details',
      title: i18n_t('操作内容'),
      ellipsis: true,
      width: i18n.locale === 'zh-CN' ? 120 : 140,
    },
    {
      colKey: 'IP',
      title: i18n_t('IP地址'),
      ellipsis: true,
      width: 160,
    },
    {
      colKey: 'device',
      title: i18n_t('操作终端'),
      ellipsis: true,
      width: i18n.locale === 'zh-CN' ? 120 : 140,
    },
  ];
  // 扁平化存储团队部分信息，便于输出团队名称路径 & 绕开 t-design 的显示 bug
  const teamMap: {
    name: string;
    parentId: SMH.TeamId;
  }[] = [];
  const teamOptions: Ref<SpaceOptionTeamChild[]> = ref([]);
  const spaceOptions: ComputedRef<SpaceOption[]> = computed(() => {
    if (isAdminOrSuperAdmin.value) {
      return [
        {
          label: i18n_t('全部'),
          value: 'all',
        },
        {
          label: i18n_t('个人空间'),
          value: 'personal',
        },
        {
          label: i18n_t('协作空间'),
          value: 'group',
        },
        {
          label: i18n_t('团队空间'),
          value: 'team',
          children: true,
        },
      ];
    }
    return teamOptions.value;
  });
  const actionList = [
    {
      label: i18n_t('全部类型'),
      value: '',
    },
    {
      label: i18n_t('登录'),
      value: 'Login',
    },
    {
      label: i18n_t('文件相关'),
      value: 'FileAction',
    },
    {
      label: i18n_t('同步盘操作'),
      value: 'SyncAction',
    },
    {
      label: i18n_t('分享相关'),
      value: 'ShareAction',
    },
    {
      label: i18n_t('审批相关'),
      value: 'AuditAction',
    },
    {
      label: i18n_t('共享相关'),
      value: 'AuthorityAction',
    },
    {
      label: i18n_t('Special.用户管理'),
      value: 'UserManagement',
    },
    {
      label: i18n_t('Special.团队管理'),
      value: 'TeamManagement',
    },
  ];
  const today = dayjs().format('YYYY-MM-DD HH:mm:ss');
  const firstDay = dayjs(today).subtract(30, 'days').format('YYYY-MM-DD 00:00:00');
  const timeRange = ref([firstDay, today]);
  const conditions = reactive<SMH.CreateSearchLogTaskParams>({
    keyword: '',
    actionType: '',
    startTime: `${dayjs(firstDay).format('YYYY-MM-DD HH:mm:ss')}`,
    endTime: `${dayjs(today).format('YYYY-MM-DD HH:mm:ss')}`,
    operator: '',
    spaceTag: isAdminOrSuperAdmin.value ? 'all' : '',
  });

  // 今天以后的日期不可选
  const disabledDate = dayjs(today).format('YYYY-MM-DD');
  const tableData = reactive<TableData>({
    searchId: '',
    loading: false,
    hasMore: false,
    searchFinished: false,
    nextMarker: -1,
    list: [],
  });

  // 记录上一次查询的marker
  const preSearchMarker = ref(-1);
  const loadingStatus = computed(() => {
    if (tableData.list.length > 0 && tableData.loading) {
      return 'loading';
    }
    if (tableData.searchFinished && tableData.hasMore) {
      return 'load-more';
    }
    return null;
  });
  const showExportingMsg = ref(false);

  let loopCount = 0;
  /**
   * 本地日志下载列表
   */
  const localTransferList = reactive<DownloadLogTransferList>({
    exporting: [],
    exported: [],
  });
  const methods = {
    initTableData() {
      Object.assign(tableData, {
        searchId: '',
        loading: false,
        hasMore: false,
        searchFinished: false,
        nextMarker: -1,
        list: [],
      });
      preSearchMarker.value = -1;
    },
    // 点击导出
    startSearch() {
      methods.initTableData();
      methods.loopSearchTask({});
    },
    // 开启一个导出任务
    async startSearchTask(params?: {}) {
      tableData.loading = true;
      try {
        const newParams = {
          ...conditions,
          ...params,
        };
        // 对 选择具体团队 的情况进行处理
        if (!['all', 'personal', 'group', 'team'].includes(newParams.spaceTag)) {
          newParams.teamId = parseInt(newParams.spaceTag, 10);
          newParams.spaceTag = 'team';
        }
        const res = await smh.userAPI.createSearchLogTask(newParams);
        const { data } = res;
        tableData.searchFinished = data.searchFinished as boolean;
        if (tableData.searchFinished || loopCount >= 3) {
          tableData.loading = false;
          loopCount = 0;
        }
        tableData.searchId = data.searchId;
        tableData.hasMore = data.hasMore;
        tableData.nextMarker = data.nextMarker;
        // 后端返回日志可能重复，id相同时table组件报错，先对id兼容处理
        tableData.list = [...tableData.list, ...data.contents].map((item: SMH.SearchLogItem, index) => ({
          ...item,
          id: `${item.id}_${index}`,
          operationTime: dayjs(item.operationTime).format('YYYY-MM-DD HH:mm:ss'),
        }));
      } catch (e) {
        tableData.loading = false;
        tableData.searchFinished = true;
        tips.error(e as MsgError);
      }
    },
    async loopSearchTask(params?: {}) {
      let intervalTimer: number | undefined;
      const stopLoop = () => {
        window.clearInterval(intervalTimer);
        intervalTimer = undefined;
      };
      const startLoop = async () => {
        await methods.startSearchTask(params);
        intervalTimer = window.setInterval(async () => {
          // 当searchFinised为false轮询查询搜索任务
          if (tableData.searchFinished || loopCount >= 3) {
            stopLoop();
          } else {
            methods.startSearchTask(params);
            loopCount++;
            if (loopCount >= 3) {
              stopLoop();
            }
          }
        }, 2000);
      };
      startLoop();
    },
    async continueSearch() {
      // 避免重复加载同一个marker
      if (tableData.loading) return;
      if (!tableData.hasMore || tableData.nextMarker < 0) return;
      if (tableData.nextMarker === preSearchMarker.value) return;
      tableData.loading = true;
      try {
        const res = await smh.userAPI.listSearchLog({
          searchId: tableData.searchId,
          marker: tableData.nextMarker,
        });
        preSearchMarker.value = tableData.nextMarker;
        tableData.loading = false;
        const { data } = res;
        // 当前搜索任务结束标志
        const searchTaskFinished = (data.hasMore && data.contents && data.contents.length === 0) || !data.hasMore;
        tableData.hasMore = data.hasMore;
        tableData.nextMarker = data.nextMarker;
        tableData.list.push(
          ...[...data.contents].map((item: SMH.SearchLogItem, index) => ({
            ...item,
            id: `${item.id}_${index}`,
            operationTime: dayjs(item.operationTime).format('YYYY-MM-DD HH:mm:ss'),
          })),
        );
        // 搜索任务达到上限且还有日志没有加载完，需要重新开启一个搜索任务
        if (!data.allResultCovered && searchTaskFinished) {
          const { id, logType, operationTime } = data.lastHit;
          methods.loopSearchTask({
            searchAfter: {
              id,
              logType,
              operationTime,
            },
            endTime: data.lastHit.operationTime,
          });
        }
      } catch (e) {
        tableData.loading = false;
      }
    },
    async cancelSearch() {
      await smh.userAPI.delSearchInLog({
        searchId: tableData.searchId,
      });
    },
    onScroll(params: { e: WheelEvent }) {
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      if (clientHeight + scrollTop >= scrollHeight - 100) {
        methods.continueSearch();
      }
    },
    initConditions() {
      Object.assign(conditions, {
        keyword: '',
        actionType: '',
        startTime: '',
        endTime: '',
        operator: '',
        spaceTag: isAdminOrSuperAdmin.value ? 'all' : '',
      });
      const firstDay = dayjs().subtract(30, 'days').format();
      const today = dayjs().format();
      timeRange.value = [firstDay, today];
    },
    // 导出全量日志
    exportAll() {
      logExport.export(columns, conditions);
    },
    // 导出当前列表
    async exportList() {
      const timeRangeStr =
        `${dayjs((conditions as SMH.CreateSearchLogTaskParams).startTime).format('YYYY-MM-DD')}_` +
        `${dayjs((conditions as SMH.CreateSearchLogTaskParams).endTime).format('YYYY-MM-DD')}`;
      const exportName = `tcedrive_log__${timeRangeStr}.csv`;
      let isSuccess = false;
      if (IS_BROWSER) {
        isSuccess = exportCSVInBrowser(columns, tableData.list, exportName);
      } else {
        const downloadPath = await getDownloadPath();
        isSuccess = exportCSVInPC(columns, tableData.list, exportName, downloadPath);
      }
      if (isSuccess) {
        localTransferList.exported.push({
          name: exportName,
          status: 200,
          taskId: new Date().getTime(),
          total: tableData.list.length,
          remaining: tableData.list.length,
          creationTime: timeRangeStr,
          target: 'local',
        });
      } else {
        localTransferList.exported.push({
          name: exportName,
          status: 500,
          taskId: new Date().getTime(),
          total: tableData.list.length,
          remaining: 0,
          creationTime: timeRangeStr,
          target: 'local',
        });
      }
    },
    async export2Cloud(spaceId: string, dirPath: string) {
      const innerSpaceId = spaceId || (await spaceATM.get()).spaceId;
      try {
        tips.loading(i18n_t('正在创建导出任务，请稍后'), 0);
        const searchParams = {
          ...conditions,
        };
        // 对 选择具体团队 的情况进行处理
        if (!['all', 'personal', 'group', 'team'].includes(searchParams.spaceTag)) {
          searchParams.teamId = parseInt(searchParams.spaceTag, 10);
          searchParams.spaceTag = 'team';
        }
        await smh.userAPI.createDownloadLogTask({
          data: {
            searchParams,
            targetSpaceId: innerSpaceId,
            targetDirectory: dirPath,
          },
        });
        tips.closeAll();
        showExportingMsg.value = true;
        setTimeout(() => {
          showExportingMsg.value = false;
        }, 5000);
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    /**
     * 从远程获取团队树/节点的值，
     * 返回值会被赋值给 teamNode 或整个团队树（无 teamNode 时）。
     */
    pullTeamNode: async (param?: TreeNodeModel<any>): Promise<SpaceOptionTeamChild[]> => {
      const { value } = param?.data || {
        value: 'team',
      };
      const teamId = ['all', 'personal', 'team'].includes(value) ? '' : value;
      const checkManagementPermission = await getCheckManagementPermission();
      const parmas: SMH.QueryTeamParams = {
        teamId,
        checkPermission: 0,
        checkManagementPermission,
      };
      try {
        const res = await smh.userAPI.queryTeam(parmas);
        const { data } = res;
        teamMap[data.id] = {
          name: data.name,
          parentId: data.parentId,
        };
        const children: SpaceOptionTeamChild[] = data.children.map(child => {
          const { id, name, children, parentId } = child;
          teamMap[id] = {
            name,
            parentId,
          };
          return {
            label: name,
            value: id.toString(),
            parentId,
            children: !!children.length,
          };
        });
        return children;
      } catch (error) {
        tips.error(error as MsgError);
        return [];
      }
    },
    setTeamOptions: async () => {
      const data = await methods.pullTeamNode();
      teamOptions.value = data;
    },
    /**
     * @description
     * @param id 团队 ID
     * @returns 团队名/父团队名/.../根团队名
     */
    getTeamNamePath(id: SMH.TeamId): string {
      if (!teamMap[id]) return '';
      const { name, parentId } = teamMap[id];
      if (parentId) return `${name}/${methods.getTeamNamePath(parentId)}`;
      return name;
    },
  };
  watch(timeRange, val => {
    if (val.length > 0) {
      const startTime = val[0];
      const endTime = val[1];
      conditions.startTime = `${dayjs(startTime).format('YYYY-MM-DD HH:mm:ss')}`;
      conditions.endTime = `${dayjs(endTime).format('YYYY-MM-DD HH:mm:ss')}`;
    }
  });
  onMounted(() => {
    showExportingMsg.value = false;
  });
  if (isTeamAdmin.value) {
    methods.setTeamOptions();
  }
  return {
    spaceOptions,
    actionList,
    disabledDate,
    timeRange,
    conditions,
    columns,
    tableData,
    loadingStatus,
    showExportingMsg,
    methods,
    localTransferList,
    isAdminOrSuperAdmin,
  };
}
